import { useEffect, useState, useRef } from 'react';
import { X, Download, AlertTriangle, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

/**
 * Vertrag PDF-Preview Modal (#1205, v2.519.0).
 * Lädt PDF als Blob via fetch (credentials:'include') und zeigt es in iframe.
 * Drei Aufruf-Modi:
 *   - { mode: 'post', endpoint, body }   → POST /api/vertraege/preview mit Form-Werten
 *   - { mode: 'get',  endpoint }         → GET (template- oder vertrag-Preview)
 */
export default function VertragPreviewModal({ open, title, mode, endpoint, body, onClose, downloadFileName }) {
  const { t } = useLanguage();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let aborted = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setErrorDetails(null);
      try {
        const init = { credentials: 'include' };
        if (mode === 'post') {
          init.method = 'POST';
          init.headers = { 'Content-Type': 'application/json' };
          init.body = JSON.stringify(body || {});
        }
        const res = await fetch(`${API}${endpoint}`, init);
        if (!res.ok) {
          let data = {};
          try { data = await res.json(); } catch { /* not JSON */ }
          throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { details: data });
        }
        const blob = await res.blob();
        if (aborted) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setPdfUrl(url);
      } catch (err) {
        if (aborted) return;
        setError(err.message || 'Fehler beim Laden');
        setErrorDetails(err.details || null);
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();

    return () => {
      aborted = true;
    };
  }, [open, mode, endpoint, JSON.stringify(body || {})]);

  // Esc-Key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Cleanup blob URL on unmount / close
  useEffect(() => {
    if (!open && blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
      setPdfUrl(null);
    }
  }, [open]);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = downloadFileName || 'vertrag-preview.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card,#E5E7EB)] flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">
              {title || (t('vertrag.previewTitle') || 'PDF-Vorschau')}
            </h2>
            <p className="text-xs text-[var(--text-muted,#9CA3AF)]">{t('vertrag.previewSubtitle') || 'Read-only — Datei nicht gespeichert'}</p>
          </div>
          <div className="flex items-center gap-2">
            {pdfUrl && !error && (
              <button
                onClick={handleDownload}
                className="px-3 py-1.5 rounded-[10px] border border-[var(--border-default,#E5E7EB)] text-xs font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)] flex items-center gap-1.5"
              >
                <Download size={14} /> {t('vertrag.downloadPdf') || 'PDF herunterladen'}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-inset,#F8F9FC)] rounded-lg"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 bg-[var(--bg-inset,#F8F9FC)] overflow-hidden">
          {loading && (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <div className="animate-spin h-8 w-8 border-2 border-[#338edc] border-t-transparent rounded-full" />
              <p className="text-sm text-[var(--text-tertiary,#6B7280)]">{t('vertrag.previewLoading') || 'PDF wird generiert ...'}</p>
            </div>
          )}
          {error && !loading && (
            <div className="flex items-center justify-center h-full p-6">
              <div className="max-w-md bg-white border border-red-200 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-1">
                      {t('vertrag.previewError') || 'PDF konnte nicht generiert werden'}
                    </h3>
                    <p className="text-sm text-[var(--text-secondary,#4B5563)]">{error}</p>
                    {errorDetails?.unbound && Array.isArray(errorDetails.unbound) && errorDetails.unbound.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">
                          {t('vertrag.previewUnbound') || 'Fehlende Platzhalter:'}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {errorDetails.unbound.map((u) => (
                            <span key={u} className="text-[11px] font-mono bg-red-50 text-red-700 px-1.5 py-0.5 rounded">
                              {`{{${u}}}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {pdfUrl && !error && !loading && (
            <iframe
              src={pdfUrl}
              title="PDF Preview"
              className="w-full h-full border-0"
            />
          )}
        </div>
      </div>
    </div>
  );
}
